import { useAppDispatch } from '@hooks/useDispatch';
import { NumberInput, Select } from '@mantine/core';
import { cellValueChanged } from '@store/Spreadsheet/cellChangesSlice';
import { setEditingCell } from '@store/Spreadsheet/editingSlice';
import { updateRow } from '@store/Spreadsheet/rowsSlice';
import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { CommonCellProps } from '../Cell';
import classes from './CellMultiNumberUnit.module.css';
import { useTranslation } from 'react-i18next';

interface CellMultiNumberUnitProps extends CommonCellProps {
  options?: string[];
}

export default function CellMultiNumberUnit({ rowId, columnId, value, currentRow, isEditing, options = []}: CellMultiNumberUnitProps ) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation( 'grid' );
  const containerRef = useRef<HTMLDivElement>( null );

  const parseTokens = useCallback(( val: any ) => {
    if ( typeof val === 'string' ) {
      return val
        .split( ',' )
        .map(( token ) => token.trim())
        .filter( Boolean );
    } else if ( Array.isArray( val )) {
      return val;
    }
    return [];
  }, []);

  const [ tokens, setTokens ] = useState<string[]>([]);

  const [ tempNumber, setTempNumber ] = useState<string | ''>( '' );
  const [ tempUnit, setTempUnit ] = useState( options[0] || '' );

  useEffect(() => {
    setTokens( parseTokens( value ));
  }, [ value, parseTokens ]);

  const commitEdit = useCallback(( finalTokens: string[]) => {
      if ( !currentRow ) return;
      const newValue = finalTokens.join( ', ' );
      const newValues = { ...currentRow.values, [columnId]: newValue };

      dispatch( updateRow({ id: rowId, changes: { values: newValues }}));
      dispatch(
        cellValueChanged({
          rowId,
          columnId,
          oldValue: currentRow.values[columnId],
          newValue
        })
      );
      dispatch( setEditingCell( null ));
    },
    [ dispatch, rowId, columnId, currentRow ]);

  const cancelEdit = useCallback(() => {
    dispatch( setEditingCell( null ));
  }, [dispatch]);

  const handleKeyDownContainer = useCallback(
    ( e: KeyboardEvent<HTMLDivElement> ) => {
      if ( !isEditing ) return;
      if ( e.key === 'Escape' ) {
        e.preventDefault();
        cancelEdit();
      }
    },
    [ isEditing, cancelEdit ]
  );

  const handleBlurContainer = useCallback(
    ( e: React.FocusEvent<HTMLDivElement> ) => {
      if ( !containerRef.current ) return;
      if ( !e.relatedTarget || !containerRef.current.contains( e.relatedTarget )) {
        commitEdit( tokens );
      }
    },
    [ commitEdit, tokens ]
  );

  const handleAddToken = useCallback(() => {
    if ( tempNumber !== '' && !Number.isNaN( Number( tempNumber )) && tempUnit ) {
      const newToken = `${tempNumber} ${tempUnit}`;
      setTokens(( prev ) => [ ...prev, newToken ]);
      setTempNumber( '' );
      setTempUnit( options[0] || '' );
    }
  }, [ tempNumber, tempUnit, options ]);

  const handleRemoveToken = useCallback(( idx: number ) => {
    setTokens(( prev ) => prev.filter(( _, i ) => i !== idx ));
  }, []);


  // read
  if ( !isEditing ) {
    return (
      <span className={classes.cellMultiUnitNumberWrapper}>
        {tokens.map(( token, idx ) => (
          <span key={idx} className={classes.cellMultiUnitNumberPill}>
            {token}
          </span>
        ))}
      </span>
    );
  }

  // edit
  return (
    <div
      ref={containerRef}
      className={classes.cellMultiUnitNumberEdit}
      tabIndex={-1}
      onKeyDown={handleKeyDownContainer}
      onBlur={handleBlurContainer}
    >
      <div className={classes.cellMultiUnitNumberAddRow}>
        <NumberInput
          classNames={{ input: classes.cellMultiUnitNumberInput }}
          value={tempNumber}
          onChange={( val ) => setTempNumber( val.toString())}
          hideControls
          placeholder={t( 'quantity' )}
          size="xs"
          variant="unstyled"
        />

        <Select
          className={classes.cellMultiUnitNumberSelect}
          data={options}
          value={tempUnit}
          onChange={( val ) => {
            if ( val ) setTempUnit( val );
          }}
          size="xs"
          placeholder={t( 'unit' )}
          variant="unstyled"
          withCheckIcon={false}
        />

        <div
          onMouseDown={( e ) => {
            e.preventDefault();
            e.stopPropagation();
            handleAddToken();
          }}
          className={classes.cellMultiUnitNumberAddButton}
        >
          +
        </div>
      </div>

      <div className={classes.cellMultiUnitNumberTokensLine}>
        {tokens.map(( token, idx ) => (
          <span key={idx} className={classes.cellMultiUnitNumberEditPill}>
            {token}
            <span
              className={classes.cellMultiUnitNumberRemoveBtn}
              onMouseDown={( e ) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemoveToken( idx );
              }}
            >
              ×
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
